<template>
  <div class="closing-investments-table">
    <div v-if="loading" class="text-center">
      <div class="spinner">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
      </div>
      <p>Loading investments...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      <strong>Error:</strong> {{ error }}
      <button @click="loadInvestments" class="btn btn-sm btn-primary ml-2">
        Retry
      </button>
    </div>

    <div v-else>
      <form 
        method="post" 
        :action="updateUrl"
        @submit="handleFormSubmit"
      >
        <input type="hidden" name="_token" :value="csrfToken">
        
        <table class="table table-hover table-striped table-condensed tranches">
          <thead>
            <tr>
              <th>Status</th>
              <th>Date</th>
              <th>No. of Investors</th>
              <th>Aggregate Subscriptions</th>
              <th>Download</th>
              <th>Action</th>
              <th v-if="offering.emd && offering.emd.is_non_brokered"></th>
              <th>Subscriber Details</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(tranche, index) in tranches">
              <tr 
                :key="tranche.key + '-' + tranche.date + '-main'"
                class="tranch-rollup"
                @click="toggleTranche(index)"
                style="cursor: pointer"
                :data-target="'.packageDetails' + tranche.slug"
              >
                <td class="tranche-status">{{ tranche.key }}</td>
                <td>{{ formatDate(tranche.date) }}</td>
                <td>{{ tranche.investments.length }}</td>
                <td>${{ formatCurrency(tranche.total) }}</td>
                <td>
                  <button
                    class="btn btn-sm btn-default"
                    type="submit"
                    name="download"
                    :value="getDownloadValue(tranche)"
                  >
                    Documents .ZIP
                  </button>
                </td>
                <td>
                  <template v-if="tranche.canBeSentToIssuer">
                    <button
                      class="btn btn-sm btn-default"
                      type="submit"
                      name="submit"
                      :value="tranche.formKey + '.' + tranche.date"
                    >
                      Send For Approval
                    </button>
                  </template>
                  
                  <template v-else-if="tranche.canCompletePayment">
                    <button
                      class="btn btn-sm btn-default"
                      type="submit"
                      name="submit"
                      :value="tranche.formKey + '.' + tranche.date"
                    >
                      Confirm Final Approval
                    </button>
                  </template>
                  
                  <template v-else-if="tranche.canCompleteFunding">
                    <button
                      class="btn btn-sm btn-default"
                      type="submit"
                      name="submit"
                      :value="tranche.formKey + '.' + tranche.date"
                    >
                      Confirm Funding
                    </button>
                  </template>
                  
                  <template v-else-if="tranche.canCompleteClosing">
                    <complete-close></complete-close>
                  </template>
                  
                  <template v-else-if="tranche.canSendClosingEmail">
                    <button
                      class="btn btn-sm btn-default"
                      type="submit"
                      name="submit"
                      :value="tranche.formKey + '.' + tranche.date"
                    >
                      {{ tranche.canSendClosingEmail ? 'Send' : 'ReSend' }} Closing Email
                    </button>
                    
                    <template v-if="offering.is_brokered">
                      <button
                        class="btn btn-sm btn-default"
                        type="submit"
                        name="submit_alt"
                        :value="tranche.formKey + '.' + tranche.date"
                      >
                        {{ tranche.canSendClosingEmail ? 'Send' : 'ReSend' }} Alt Closing Email
                      </button>
                    </template>
                    
                    <template v-if="offering.can_counter_sign && isCounterSigner">
                      <button
                        class="btn btn-sm btn-default"
                        type="submit"
                        name="counter_sign"
                        :value="tranche.formKey + '.' + tranche.date"
                      >
                        Countersign Subscriptions
                      </button>
                    </template>
                  </template>
                </td>
                
                <td v-if="offering.emd && offering.emd.is_non_brokered">
                  <div v-if="!hasEmailContent" class="alert alert-warning">
                    <strong>Warning!</strong> Please add the email content for this offering.
                  </div>
                </td>
                
                <td class="text-center">
                  <i :class="tranche.expanded ? 'glyphicon glyphicon-chevron-down' : 'glyphicon glyphicon-chevron-right'" class="indicator"></i>
                </td>
              </tr>
              
              <tr v-if="tranche.expanded" :key="tranche.key + '-' + tranche.date + '-details'" :data-investments="tranche.formKey">
                <td colspan="8" class="hiddenRow">
                  <div :class="'accordion-body collapse in packageDetails' + tranche.slug">
                    <div class="tranche-controls mb-2">
                      <button 
                        type="button" 
                        class="btn btn-xs btn-default"
                        @click="toggleAllInTranche(tranche)"
                      >
                        Toggle All
                      </button>
                      <span class="ml-2 text-muted">
                        {{ getCheckedInvestmentsForTranche(tranche).length }} of {{ tranche.investments.length }} selected
                      </span>
                    </div>
                    <table class="table offering-closing">
                      <thead>
                        <tr>
                          <th></th>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Investment Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="investment in tranche.investments" :key="investment.id" class="investment">
                          <td>
                            <input
                              type="checkbox"
                              :name="tranche.formKey + '[i_' + investment.id + ']'"
                              :checked="checkedInvestments[tranche.formKey + '[i_' + investment.id + ']']"
                              @change="toggleInvestmentCheckbox(tranche.formKey, investment.id)"
                            />
                          </td>
                          <td>
                            <a :href="getConsoleInvestmentUrl(investment.id)">
                              {{ investment.id }}
                            </a>
                          </td>
                          <td>{{ investment.subscriber_name }}</td>
                          <td>${{ formatCurrency(investment.amount) }}</td>
                          <td>
                            <span v-if="offering.is_brokered" :class="getInvestmentStatusClass(investment.status)">
                              {{ getInvestmentStatusText(investment.status) }}
                            </span>
                            <span v-else :class="getNonInvestmentStatusClass(tranche.formKey)">
                              {{ getNonInvestmentStatusText(tranche.formKey) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        
        <div v-if="pagination && pagination.last_page > 1" class="text-center">
          <!-- REPLACE THE EXISTING PAGINATION INFO WITH ACTUAL BUTTONS -->
          <nav aria-label="Investments pagination">
            <ul class="pagination">
              <!-- Previous button -->
              <li :class="{ disabled: pagination.current_page === 1 }">
                <a 
                  href="#" 
                  @click.prevent="changePage(pagination.current_page - 1)"
                  :disabled="pagination.current_page === 1"
                >
                  &laquo; Previous
                </a>
              </li>
              
              <!-- Page numbers -->
              <li 
                v-for="page in getPageNumbers()" 
                :key="page"
                :class="{ active: page === pagination.current_page }"
              >
                <a 
                  href="#" 
                  @click.prevent="changePage(page)"
                  v-if="page !== '...'"
                >
                  {{ page }}
                </a>
                <span v-else>{{ page }}</span>
              </li>
              
              <!-- Next button -->
              <li :class="{ disabled: pagination.current_page === pagination.last_page }">
                <a 
                  href="#" 
                  @click.prevent="changePage(pagination.current_page + 1)"
                  :disabled="pagination.current_page === pagination.last_page"
                >
                  Next &raquo;
                </a>
              </li>
            </ul>
          </nav>
          
          <!-- Show page info -->
          <p class="text-muted">
            Page {{ pagination.current_page }} of {{ pagination.last_page }} 
            ({{ pagination.total }} total investments)
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import CompleteClose from '../CompleteClose.vue'

export default {
  name: 'ClosingInvestmentsTable',
  components: {
    CompleteClose
  },
  props: {
    offeringId: {
      type: Number,
      required: true
    },
    emdSlug: {
      type: String,
      required: true
    },
    offering: {
      type: Object,
      required: true
    },
    isCounterSigner: {
      type: Boolean,
      default: false
    },
    hasEmailContent: {
      type: Boolean,
      default: true
    },
    updateUrl: {
      type: String,
      required: true
    },
    csrfToken: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      investments: [],
      tranches: [],
      pagination: null,
      currentPage: 1,
      checkedInvestments: {}
    }
  },
  mounted() {
    this.loadInvestments()
    this.initializeAccordion()
  },
  methods: {
    async loadInvestments(page = 1) {
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.get(
          `/admin/${this.emdSlug}/offerings/${this.offeringId}/closing/api/investments`,
          { 
            params: { 
              page: page,
              per_page: 50 
            } 
          }
        )
        
        this.investments = response.data.investments || []
        this.pagination = response.data.pagination || null
        this.currentPage = page
        
        this.organizeTranches()
        
      } catch (error) {
        this.error = 'Failed to load investments. Please try again.'
        console.error('Error loading investments:', error)
      } finally {
        this.loading = false
      }
    },
    
    organizeTranches() {
      const trancheMap = new Map()
      
      this.investments.forEach(investment => {
        const status = investment.status
        let key
        
        if (status === 'complete') {
          if (investment.closed_on) {
            const datePart = investment.closed_on.split('T')[0].split(' ')[0];
            key = `complete_${datePart}`;
          } else {
            key = 'complete_no-date';
          }
        } else if (status === 'counter signed') {
          if (investment.closed_on && investment.counter_signed_on) {
            const datePart = investment.closed_on.split('T')[0].split(' ')[0];
            key = `countersigned_${datePart}`;
          } else {
            key = 'countersigned_no-date';
          }
        } else {
          key = status;
        }
        
        if (!trancheMap.has(key)) {
          let trancheDate = null
          if (status === 'complete' || status === 'counter signed') {
            trancheDate = investment.closed_on ? investment.closed_on.split('T')[0].split(' ')[0] : null
          }
          
          trancheMap.set(key, {
            key: status,
            date: trancheDate,
            formKey: status.replace(/\s+/g, '_'),
            slug: status.replace(/\s+/g, '-').toLowerCase(),
            investments: [],
            expanded: false,
            canBeSentToIssuer: status === 'pending delivery for approval',
            canCompletePayment: status === 'pending final approval',
            canCompleteFunding: status === 'pending funding',
            canCompleteClosing: status === 'pending close',
            canTriggerClosing: status === 'pending close',
            canSendClosingEmail: (status === 'complete' || status === 'counter signed') && !investment.closing_email_sent
          })
        }
        
        trancheMap.get(key).investments.push(investment)
        
        // Initialize checkbox state (all checked by default)
        const checkboxKey = `${status.replace(/\s+/g, '_')}[i_${investment.id}]`
        this.$set(this.checkedInvestments, checkboxKey, true)
      })
      
      const allowedStatuses = [
        'pending delivery for approval',
        'pending final approval',
        'pending funding', 
        'pending close',
        'complete',
        'counter signed'
      ]
      
      this.tranches = Array.from(trancheMap.values())
        .filter(tranche => allowedStatuses.includes(tranche.key))
        .map(tranche => ({
          ...tranche,
          total: tranche.investments.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0)
        }))
    },
    
    initializeAccordion() {
      this.$nextTick(() => {
        // This will be handled by the toggleTranche method instead of jQuery
      })
    },
    
    toggleTranche(index) {
      if (this.tranches[index]) {
        this.$set(this.tranches[index], 'expanded', !this.tranches[index].expanded)
      }
    },
    
    getConsoleInvestmentUrl(investmentId) {
      return `/admin/${this.emdSlug}/subscriptions/${investmentId}`
    },
    
    getInvestmentStatusClass(status) {
      const statusMap = {
        'pending close': 'label label-warning',
        'incomplete': 'label label-default',
        'pending signature': 'label label-default',
        'in progress': 'label label-default',
        'pending final approval': 'label label-warning',
        'cancelled': 'label label-danger',
        'pending dr approval': 'label label-warning',
        'pending cco approval': 'label label-warning',
        'pending delivery for approval': 'label label-warning',
        'pending approval': 'label label-warning',
        'pending id verification': 'label label-warning',
        'rejected': 'label label-danger',
        'complete': 'label label-success',
        'counter signed': 'label label-success',
        'approved': 'label label-success',
        'pending funding': 'label label-warning',
        'funded': 'label label-warning'
      }
      
      return statusMap[status] || 'label label-warning'
    },
    
    getInvestmentStatusText(status) {
      const statusTextMap = {
        'pending close': 'Pending Close',
        'incomplete': 'Incomplete',
        'pending signature': 'Pending Signature',
        'in progress': 'In Progress',
        'pending final approval': 'Pending Final Approval',
        'cancelled': 'Cancelled',
        'pending dr approval': 'Pending DR Approval',
        'pending cco approval': 'Pending CCO Approval',
        'pending delivery for approval': 'Pending Delivery For Approval',
        'pending approval': 'Pending Approval',
        'pending id verification': 'Pending ID Verification',
        'rejected': 'Rejected',
        'complete': 'Complete',
        'counter signed': 'Counter Signed',
        'approved': 'Approved',
        'pending funding': 'Pending Funding',
        'funded': 'Pending Close'
      }
      
      return statusTextMap[status] || status
    },
    
    getNonInvestmentStatusClass(trancheKey) {
      const statusMap = {
        'pending_close': 'label label-warning',
        'cancelled': 'label label-danger',
        'pending_approval': 'label label-warning',
        'complete': 'label label-success',
        'approved': 'label label-success',
        'pending_funding': 'label label-warning',
        'funded': 'label label-warning',
        'rejected': 'label label-danger'
      }
      
      return statusMap[trancheKey] || 'label label-default'
    },
    
    getNonInvestmentStatusText(trancheKey) {
      const statusTextMap = {
        'pending_close': 'Pending Close',
        'cancelled': 'Cancelled',
        'pending_approval': 'Pending Approval',
        'complete': 'Complete',
        'approved': 'Approved',
        'pending_funding': 'Pending Funding',
        'funded': 'Pending Close',
        'rejected': 'Rejected'
      }
      
      return statusTextMap[trancheKey] || trancheKey
    },
    
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0)
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString
      }
      
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      
      return `${year}-${month}-${day}`
    },
    
    toggleInvestmentCheckbox(trancheFormKey, investmentId) {
      const checkboxKey = `${trancheFormKey}[i_${investmentId}]`
      this.$set(this.checkedInvestments, checkboxKey, !this.checkedInvestments[checkboxKey])
    },
    
    toggleAllInTranche(tranche) {
      const allChecked = tranche.investments.every(investment => {
        const checkboxKey = `${tranche.formKey}[i_${investment.id}]`
        return this.checkedInvestments[checkboxKey]
      })
      
      // If all are checked, uncheck all; otherwise check all
      const newState = !allChecked
      
      tranche.investments.forEach(investment => {
        const checkboxKey = `${tranche.formKey}[i_${investment.id}]`
        this.$set(this.checkedInvestments, checkboxKey, newState)
      })
    },
    
    getCheckedInvestmentsForTranche(tranche) {
      return tranche.investments.filter(investment => {
        const checkboxKey = `${tranche.formKey}[i_${investment.id}]`
        return this.checkedInvestments[checkboxKey]
      })
    },
    
    handleFormSubmit(event) {
      console.log('Form submitted!', event.target)
      
      // Get the submit button that was clicked
      const submitButton = event.submitter
      if (!submitButton) return
      
      const buttonValue = submitButton.value
      const [trancheKey, action] = buttonValue.split('.')
      
      // Find the relevant tranche
      const tranche = this.tranches.find(t => t.formKey === trancheKey)
      if (!tranche) return
      
      // Check if any investments are selected for this tranche
      const checkedInvestments = this.getCheckedInvestmentsForTranche(tranche)
      
      if (checkedInvestments.length === 0) {
        event.preventDefault()
        alert('Please select at least one investment to process.')
        return false
      }
      
      // Add hidden inputs for the checked investments and action
      const form = event.target
      
      // Remove any existing hidden inputs from previous submissions
      form.querySelectorAll('input[name="investment_ids[]"], input[name="action"]').forEach(input => {
        input.remove()
      })
      
      // Add action input
      const actionInput = document.createElement('input')
      actionInput.type = 'hidden'
      actionInput.name = 'action'
      actionInput.value = action
      form.appendChild(actionInput)
      
      // Add investment IDs
      checkedInvestments.forEach(investment => {
        const idInput = document.createElement('input')
        idInput.type = 'hidden'
        idInput.name = 'investment_ids[]'
        idInput.value = investment.id
        form.appendChild(idInput)
      })
      
      console.log('Submitting action:', action, 'with investments:', checkedInvestments.map(i => i.id))
    },
    
    getDownloadValue(tranche) {
      if (tranche.date && tranche.date !== 'no-date') {
        return `${tranche.formKey}.${tranche.date}`;
      }
      return tranche.formKey;
    },
    
    changePage(page) {
      if (page < 1 || page > this.pagination.last_page || page === this.pagination.current_page) {
        return
      }
      
      this.loadInvestments(page)
    },
    
    getPageNumbers() {
      const current = this.pagination.current_page
      const last = this.pagination.last_page
      const pages = []
      
      if (last <= 7) {
        // Show all pages if 7 or fewer
        for (let i = 1; i <= last; i++) {
          pages.push(i)
        }
      } else {
        // Show abbreviated pagination
        if (current <= 4) {
          // Near the beginning
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(last)
        } else if (current >= last - 3) {
          // Near the end
          pages.push(1)
          pages.push('...')
          for (let i = last - 4; i <= last; i++) {
            pages.push(i)
          }
        } else {
          // In the middle
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(last)
        }
      }
      
      return pages
    }
  }
}
</script>

<style scoped>
.label {
  display: inline;
  padding: .2em .6em .3em;
  font-size: 75%;
  font-weight: bold;
  line-height: 1;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: .25em;
}

.label-default {
  background-color: #777;
}

.label-success {
  background-color: #5cb85c;
}

.label-warning {
  background-color: #f0ad4e;
}

.label-danger {
  background-color: #d9534f;
}

.offering-closing {
  margin-bottom: 0;
}

.accordion-body {
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-top: none;
  padding: 0;
}

.accordion-body.collapse {
  display: block;
}

.hiddenRow {
  padding: 0 !important;
  border: none !important;
}

.investment:hover {
  background-color: #f5f5f5;
}

.spinner {
  margin: 20px 0;
}

.pagination {
  display: inline-block;
  padding-left: 0;
  margin: 20px 0;
  border-radius: 4px;
}

.pagination > li {
  display: inline;
}

.pagination > li > a,
.pagination > li > span {
  position: relative;
  float: left;
  padding: 6px 12px;
  margin-left: -1px;
  line-height: 1.42857143;
  color: #337ab7;
  text-decoration: none;
  background-color: #fff;
  border: 1px solid #ddd;
}

.pagination > li:first-child > a,
.pagination > li:first-child > span {
  margin-left: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.pagination > li:last-child > a,
.pagination > li:last-child > span {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.pagination > li > a:hover,
.pagination > li > a:focus {
  color: #23527c;
  background-color: #eee;
  border-color: #ddd;
}

.pagination > .active > a,
.pagination > .active > span {
  z-index: 2;
  color: #fff;
  cursor: default;
  background-color: #337ab7;
  border-color: #337ab7;
}

.pagination > .disabled > span,
.pagination > .disabled > a {
  color: #777;
  cursor: not-allowed;
  background-color: #fff;
  border-color: #ddd;
}

.pagination > .disabled > a:hover,
.pagination > .disabled > a:focus {
  color: #777;
  background-color: #fff;
  border-color: #ddd;
}

.tranch-rollup {
  cursor: pointer;
}

.indicator {
  font-size: 12px;
}
</style>